import { useEffect, useRef, type RefObject } from 'react';

interface UseViewabilityTrackerProps {
  onViewable: () => void;
  threshold?: number;
  once?: boolean;
}

export const useViewabilityTracker = <T extends HTMLElement>({
  onViewable,
  threshold = 0.5,
  once = true,
}: UseViewabilityTrackerProps): RefObject<T | null> => {
  const elementRef = useRef<T>(null);

  // Store the latest onViewable in a ref so the observer doesn't restart on every render.
  // Previously, passing an inline function as onViewable caused the effect to re-run on
  // every parent re-render because each render creates a new function reference.
  // This would destroy and recreate the IntersectionObserver for every product card on
  // every scroll event, causing severe main-thread jank on feed pages.
  const onViewableRef = useRef(onViewable);
  useEffect(() => {
    onViewableRef.current = onViewable;
  }, [onViewable]);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          onViewableRef.current(); // Always calls the latest callback without restarting observer
          if (once) {
            observer.disconnect();
          }
        }
      },
      { threshold }
    );

    observer.observe(element);
    return () => {
      observer.disconnect();
    };
  }, [threshold, once]); // onViewable intentionally omitted — handled via ref above

  return elementRef;
};

export default useViewabilityTracker;
